import React, { useState, useEffect, useRef, useMemo } from "react";
import "./profile.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { createPortal } from "react-dom";

/* ── Helper ── */
const getFileName = (path) => {
  if (!path) return "";
  return path.split(/[\\/]/).pop();
};

const COUNTRIES = [
  { label: "Tunisia",      value: "Tunisia" },
  { label: "France",       value: "France" },
  { label: "Germany",      value: "Germany" },
  { label: "UK",           value: "UK" },
  { label: "Australia",    value: "Australia" },
  { label: "India",        value: "India" },
  { label: "Japan",        value: "Japan" },
  { label: "USA",          value: "USA" },
  { label: "Canada",       value: "Canada" },
  { label: "Morocco",      value: "Morocco" },
  { label: "Algeria",      value: "Algeria" },
  { label: "Egypt",        value: "Egypt" },
  { label: "Qatar",        value: "Qatar" },
  { label: "Spain",        value: "Spain" },
  { label: "Italy",        value: "Italy" },
  { label: "Brazil",       value: "Brazil" },
  { label: "China",        value: "China" },
  { label: "Russia",       value: "Russia" },
  { label: "South Africa", value: "South Africa" },
  { label: "Mexico",       value: "Mexico" },
  { label: "Argentina",    value: "Argentina" },
  { label: "Saudi Arabia", value: "Saudi Arabia" },
  { label: "Sweden",       value: "Sweden" },
  { label: "Netherlands",  value: "Netherlands" },
  { label: "Turkey",       value: "Turkey" },
  { label: "Belgium",      value: "Belgium" },
  { label: "Switzerland",  value: "Switzerland" },
  { label: "South Korea",  value: "South Korea" },
  { label: "Singapore",    value: "Singapore" },
];

const GENDERS = [
  { label: "Female", value: "woman" },
  { label: "Male",   value: "man" },
];

const PHONE_CODES = [
  { label: "Afghanistan +93",          value: "+93" },
  { label: "Albania +355",             value: "+355" },
  { label: "Algeria +213",             value: "+213" },
  { label: "Argentina +54",            value: "+54" },
  { label: "Australia +61",            value: "+61" },
  { label: "Austria +43",              value: "+43" },
  { label: "Belgium +32",              value: "+32" },
  { label: "Brazil +55",               value: "+55" },
  { label: "Canada +1",                value: "+1" },
  { label: "China +86",                value: "+86" },
  { label: "Egypt +20",                value: "+20" },
  { label: "France +33",               value: "+33" },
  { label: "Germany +49",              value: "+49" },
  { label: "India +91",                value: "+91" },
  { label: "Italy +39",                value: "+39" },
  { label: "Japan +81",                value: "+81" },
  { label: "Jordan +962",              value: "+962" },
  { label: "Kuwait +965",              value: "+965" },
  { label: "Lebanon +961",             value: "+961" },
  { label: "Malaysia +60",             value: "+60" },
  { label: "Morocco +212",             value: "+212" },
  { label: "Netherlands +31",          value: "+31" },
  { label: "Nigeria +234",             value: "+234" },
  { label: "Pakistan +92",             value: "+92" },
  { label: "Palestine +970",           value: "+970" },
  { label: "Qatar +974",               value: "+974" },
  { label: "Russia +7",                value: "+7" },
  { label: "Saudi Arabia +966",        value: "+966" },
  { label: "Singapore +65",            value: "+65" },
  { label: "South Africa +27",         value: "+27" },
  { label: "South Korea +82",          value: "+82" },
  { label: "Spain +34",                value: "+34" },
  { label: "Sweden +46",               value: "+46" },
  { label: "Switzerland +41",          value: "+41" },
  { label: "Tunisia +216",             value: "+216" },
  { label: "Turkey +90",               value: "+90" },
  { label: "UAE +971",                 value: "+971" },
  { label: "UK +44",                   value: "+44" },
  { label: "Ukraine +380",             value: "+380" },
  { label: "USA +1",                   value: "+1" },
  { label: "Vietnam +84",              value: "+84" },
];

const EMPTY_PROFILE = {
  name: "", surname: "", dateOfBirth: "", gender: "",
  email: "", country: "", address: "",
  phoneNumber: "", mobile: "",
  cinFront: "", cinBack: "", video: "",
};

const EMPTY_ERRORS = { ...EMPTY_PROFILE };

/* ══════════════════════════════════════════════════════════
   Stable components, defined OUTSIDE of Profile.
   They receive everything via props → never recreated on re-render
   → the input keeps focus while typing.
   ══════════════════════════════════════════════════════════ */

/* Field: label on top, icon + value below */
const Field = ({ label, icon, error, full, disabled, children }) => (
  <div className={`pr-cell${full ? " pr-cell--full" : ""}`}>
    <label
      className={`pr-field${error ? " pr-field--error" : ""}${disabled ? " pr-field--disabled" : ""}`}
    >
      <span className="pr-field__label">{label}</span>
      <span className="pr-field__row">
        <i className={`fas ${icon} pr-field__icon`} aria-hidden="true" />
        {children}
      </span>
    </label>
    {error && <p className="pr-error">{error}</p>}
  </div>
);

/* ══════════════════════════════════════════════════════════
   Custom select (replaces the native <select>)
   - desktop / tablet : dropdown under the field (3 visible rows)
   - phone (<= 600px) : full-width panel at the bottom of the screen
   The menu is rendered in document.body: never clipped by the popup.
   Number of visible rows: --pr-select-rows in profile.css
   ══════════════════════════════════════════════════════════ */
const useIsMobile = () => {
  const query = "(max-width: 600px)";
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
};

const SelectField = ({
  label, icon, value, options, placeholder = "Select",
  error, full, searchable, triggerLabel, onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const keyboardRef = useRef(false);
  const isMobile = useIsMobile();

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const openMenu = () => {
    const r = triggerRef.current.getBoundingClientRect();
    const margin = 12;
    const maxH = 230;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const below = spaceBelow >= Math.min(maxH, 200) || spaceBelow >= spaceAbove;
    const width = Math.min(Math.max(r.width, 240), window.innerWidth - margin * 2);
    const left = Math.max(margin, Math.min(r.left, window.innerWidth - width - margin));
    setPos({
      left,
      width,
      top: below ? r.bottom + 6 : undefined,
      bottom: below ? undefined : window.innerHeight - r.top + 6,
      maxH: Math.min(maxH, below ? spaceBelow : spaceAbove),
    });
    setQuery("");
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    keyboardRef.current = true; // scrolls the selected value into view on open
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  const choose = (o) => {
    onChange(o.value);
    close();
  };

  /* Close on page scroll / resize (desktop) */
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => {
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => { if (!isMobile) setOpen(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, isMobile]);

  /* Focus: search box on desktop, panel on phone (no forced keyboard) */
  useEffect(() => {
    if (!open) return undefined;
    const id = requestAnimationFrame(() => {
      const el = searchRef.current && !isMobile ? searchRef.current : panelRef.current;
      if (el) el.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [open, isMobile]);

  /* Keep the active option visible (keyboard navigation) */
  useEffect(() => {
    if (!open || !keyboardRef.current || !listRef.current) return;
    keyboardRef.current = false;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open, query]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      keyboardRef.current = true;
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      keyboardRef.current = true;
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active]);
    }
  };

  return (
    <div className={`pr-cell${full ? " pr-cell--full" : ""}`}>
      <button
        type="button"
        ref={triggerRef}
        className={`pr-field pr-select__trigger${error ? " pr-field--error" : ""}${open ? " pr-select__trigger--open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span className="pr-field__label">{label}</span>
        <span className="pr-field__row">
          <i className={`fas ${icon} pr-field__icon`} aria-hidden="true" />
          <span className={`pr-select__value${selected ? "" : " pr-select__value--placeholder"}`}>
            {selected ? (triggerLabel ? triggerLabel(selected) : selected.label) : placeholder}
          </span>
          <i className="fas fa-chevron-down pr-select__chevron" aria-hidden="true" />
        </span>
      </button>
      {error && <p className="pr-error">{error}</p>}

      {open && pos &&
        createPortal(
          <>
            <div
              className={`pr-select__backdrop${isMobile ? " pr-select__backdrop--sheet" : ""}`}
              onClick={() => setOpen(false)}
            />
            <div
              ref={panelRef}
              tabIndex={-1}
              className={`pr-select__panel${isMobile ? " pr-select__panel--sheet" : ""}`}
              style={isMobile ? undefined : {
                left: pos.left, width: pos.width,
                top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH,
              }}
              onKeyDown={onKeyDown}
            >
              {isMobile && <div className="pr-select__handle" />}
              {isMobile && <div className="pr-select__title">{label}</div>}
              {searchable && (
                <div className="pr-select__search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    placeholder="Search…"
                    onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                  />
                </div>
              )}
              <ul ref={listRef} className="pr-select__list" role="listbox" aria-label={label}>
                {filtered.length === 0 && <li className="pr-select__empty">No results</li>}
                {filtered.map((o, i) => {
                  const isSel = o === selected;
                  return (
                    <li
                      key={`${o.label}-${i}`}
                      data-idx={i}
                      role="option"
                      aria-selected={isSel}
                      className={`pr-select__option${i === active ? " is-active" : ""}${isSel ? " is-selected" : ""}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => choose(o)}
                    >
                      <span>{o.label}</span>
                      {isSel && <i className="fas fa-check" aria-hidden="true" />}
                    </li>
                  );
                })}
              </ul>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

/* Document chip: invisible input BEFORE the label (keyboard focus works) */
const DocChip = ({ id, label, icon, fileName, error, accept, wide, onChange }) => (
  <>
    <input
      type="file"
      id={id}
      accept={accept}
      className="pr-file-input"
      onChange={onChange}
    />
    <label
      htmlFor={id}
      className={`pr-doc${fileName ? " pr-doc--done" : ""}${error ? " pr-doc--error" : ""}${wide ? " pr-doc--wide" : ""}`}
    >
      <i className={`fas ${fileName ? "fa-check" : icon} pr-doc__icon`} aria-hidden="true" />
      <span className="pr-doc__text">{fileName || label}</span>
    </label>
  </>
);

const VerifiedView = ({ profileData }) => (
  <div className="pr-body">
    <p className="pr-section-label">Personal information</p>
    <div className="pr-grid">
      <Field label="First name" icon="fa-user" disabled>
        <input type="text" value={profileData.name} disabled />
      </Field>
      <Field label="Last name" icon="fa-user" disabled>
        <input type="text" value={profileData.surname} disabled />
      </Field>
      <Field label="Date of birth" icon="fa-calendar-alt" disabled>
        <input type="date" value={profileData.dateOfBirth} disabled />
      </Field>
      <Field label="Gender" icon="fa-venus-mars" disabled>
        <select value={profileData.gender} disabled>
          <option value="">—</option>
          <option value="woman">Female</option>
          <option value="man">Male</option>
        </select>
      </Field>
      <Field label="Email" icon="fa-envelope" full disabled>
        <input type="email" value={profileData.email} disabled />
      </Field>
    </div>

    <p className="pr-section-label">Location</p>
    <div className="pr-grid">
      <Field label="Country" icon="fa-globe" disabled>
        <select value={profileData.country} disabled>
          <option value="">—</option>
          {COUNTRIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Address" icon="fa-map-marker-alt" disabled>
        <input type="text" value={profileData.address} disabled />
      </Field>
    </div>

    <p className="pr-section-label">Contact</p>
    <div className="pr-grid pr-grid--phone">
      <Field label="Dial code" icon="fa-phone" disabled>
        <select value={profileData.phoneNumber} disabled>
          <option value="">—</option>
          {PHONE_CODES.map((p) => (
            <option key={p.label} value={p.value}>{p.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Mobile" icon="fa-mobile-alt" disabled>
        <input type="text" value={profileData.mobile} disabled />
      </Field>
    </div>
  </div>
);

const FormView = ({
  profileData, errors, isSending,
  setField, handleChange, handleCountryCodeChange, handleMobileChange, handleFileChange,
  cinFrontName, cinBackName, videoName,
  handleSubmit,
}) => (
  <form className="pr-form" onSubmit={handleSubmit} noValidate>
    <div className="pr-body">
    <p className="pr-section-label">Personal information</p>
    <div className="pr-grid">
      <Field label="First name" icon="fa-user" error={errors.name}>
        <input type="text" name="name" placeholder="Your first name" value={profileData.name} onChange={handleChange} />
      </Field>
      <Field label="Last name" icon="fa-user" error={errors.surname}>
        <input type="text" name="surname" placeholder="Your last name" value={profileData.surname} onChange={handleChange} />
      </Field>
      <Field label="Date of birth" icon="fa-calendar-alt" error={errors.dateOfBirth}>
        <input type="date" name="dateOfBirth" value={profileData.dateOfBirth} onChange={handleChange} />
      </Field>
      <SelectField
        label="Gender" icon="fa-venus-mars"
        value={profileData.gender} options={GENDERS}
        error={errors.gender}
        onChange={(v) => setField("gender", v)}
      />
      <Field label="Email" icon="fa-envelope" full error={errors.email}>
        <input type="email" name="email" placeholder="name@example.com" value={profileData.email} onChange={handleChange} />
      </Field>
    </div>

    <p className="pr-section-label">Location</p>
    <div className="pr-grid">
      <SelectField
        label="Country" icon="fa-globe" searchable
        value={profileData.country} options={COUNTRIES}
        error={errors.country}
        onChange={(v) => setField("country", v)}
      />
      <Field label="Address" icon="fa-map-marker-alt" error={errors.address}>
        <input type="text" name="address" placeholder="Your address" value={profileData.address} onChange={handleChange} />
      </Field>
    </div>

    <p className="pr-section-label">Contact</p>
    <div className="pr-grid pr-grid--phone">
      <SelectField
        label="Dial code" icon="fa-phone" searchable placeholder="Code"
        value={profileData.phoneNumber} options={PHONE_CODES}
        triggerLabel={(o) => o.value}
        error={errors.phoneNumber}
        onChange={(v) => handleCountryCodeChange({ target: { value: v } })}
      />
      <Field label="Mobile" icon="fa-mobile-alt" error={errors.mobile}>
        <input type="text" name="mobile" placeholder="Number" value={profileData.mobile} onChange={handleMobileChange} />
      </Field>
    </div>

    <p className="pr-section-label">Documents</p>
    <div className="pr-docs">
      <DocChip
        id="cinFront" label="ID card front" icon="fa-file-image"
        fileName={cinFrontName} error={errors.cinFront} accept="image/*"
        onChange={(e) => handleFileChange(e, "cinFront")}
      />
      <DocChip
        id="cinBack" label="ID card back" icon="fa-file-image"
        fileName={cinBackName} error={errors.cinBack} accept="image/*"
        onChange={(e) => handleFileChange(e, "cinBack")}
      />
      <DocChip
        id="video" label="Video" icon="fa-video"
        fileName={videoName} error={errors.video} accept="video/*"
        onChange={(e) => handleFileChange(e, "video")}
      />
    </div>
    {errors.cinFront && <p className="pr-error">{errors.cinFront}</p>}
    {errors.cinBack  && <p className="pr-error">{errors.cinBack}</p>}
    {errors.video    && <p className="pr-error">{errors.video}</p>}

    </div>
    <div className="pr-footer">
      <button type="submit" className="pr-save" disabled={isSending}>
        {isSending ? "Sending…" : "Submit"}
      </button>
    </div>
  </form>
);

/* ════════════════════════════════════════════════ */
const Profile = ({ onClose }) => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [profileData,  setProfileData]  = useState(EMPTY_PROFILE);
  const [errors,       setErrors]       = useState(EMPTY_ERRORS);
  const [cinFrontName, setCinFrontName] = useState("");
  const [cinBackName,  setCinBackName]  = useState("");
  const [videoName,    setVideoName]    = useState("");
  const [isVerified,   setIsVerified]   = useState(false);
  const [isSubmitted,  setIsSubmitted]  = useState(false);
  const [isSending,    setIsSending]    = useState(false);

  /* ── Fetch ── */
  useEffect(() => {
    const userId    = localStorage.getItem("userId");
    const token     = localStorage.getItem("token");
    const userEmail = localStorage.getItem("userEmail");

    if (!userId || !token) {
      toast.error("Authentication error. Please log in again.");
      return;
    }

    setProfileData((p) => ({ ...p, email: userEmail || "" }));

    const fetchProfile = async () => {
      try {
        const { data: profile } = await axios.get(
          `${API_URL}/profiles/user/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // No profile yet (the server answers 200 { exists: false }):
        // keep the empty form without touching what the user is typing.
        if (!profile || profile.exists === false) return;

        setProfileData({
          name:        profile.name        || "",
          surname:     profile.surname     || "",
          dateOfBirth: profile.dateOfBirth
            ? new Date(profile.dateOfBirth).toISOString().split("T")[0]
            : "",
          gender:      profile.gender      || "",
          email:       profile.email       || userEmail || "",
          country:     profile.country     || "",
          address:     profile.address     || "",
          phoneNumber: profile.phoneNumber || "",
          mobile:      profile.mobile      || "",
          cinFront:    profile.cinFront    || "",
          cinBack:     profile.cinBack     || "",
          video:       profile.video       || "",
        });
        setCinFrontName(getFileName(profile.cinFront));
        setCinBackName(getFileName(profile.cinBack));
        setVideoName(getFileName(profile.video));
        setIsVerified(profile.isVerified || false);
        setIsSubmitted(true);
      } catch (err) {
        if (err.response?.status !== 404)
          console.error("Error fetching profile:", err);
      }
    };

    const fetchNotifications = async () => {
      try {
        const { data: notifs } = await axios.get(
          `${API_URL}/notificationsUser`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const rejected = notifs.find(
          (n) => n.type === "PROFILE_REJECTED" && n.userId === userId
        );
        if (rejected) {
          setIsVerified(false);
          setIsSubmitted(false);
          toast.error("Your profile was rejected. Please resubmit.");
          await axios.delete(
            `${API_URL}/notificationsUser/${rejected._id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          await fetchProfile();
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };

    fetchProfile();
    fetchNotifications();
    const ni = setInterval(fetchNotifications, 30000);
    const pi = setInterval(fetchProfile, 30000);
    return () => { clearInterval(ni); clearInterval(pi); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Handlers ── */
  const setField = (name, value) => {
    setProfileData((p) => ({ ...p, [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  const handleChange = (e) => setField(e.target.name, e.target.value);

  const handleCountryCodeChange = (e) => {
    const code = e.target.value;
    setProfileData((p) => ({ ...p, phoneNumber: code }));
    setErrors((er) => ({ ...er, phoneNumber: "" }));
  };

  const handleMobileChange = (e) => {
    setField("mobile", e.target.value.replace(/\D/g, ""));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if ((fileType === "cinFront" || fileType === "cinBack") && !isImage) {
      setErrors((er) => ({ ...er, [fileType]: "Please upload an image file." }));
      return;
    }
    if (fileType === "video" && !isVideo) {
      setErrors((er) => ({ ...er, video: "Please upload a video file." }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors((er) => ({ ...er, [fileType]: "File size must be less than 10MB." }));
      return;
    }
    setProfileData((p) => ({ ...p, [fileType]: file }));
    if (fileType === "cinFront") setCinFrontName(file.name);
    else if (fileType === "cinBack") setCinBackName(file.name);
    else if (fileType === "video") setVideoName(file.name);
    setErrors((er) => ({ ...er, [fileType]: "" }));
  };

  /* ── Validation ── */
  const validateProfile = () => {
    const e = {};
    if (!profileData.name)        e.name        = "First name is required.";
    if (!profileData.surname)     e.surname     = "Last name is required.";
    if (!profileData.dateOfBirth) e.dateOfBirth = "Date of birth is required.";
    if (!profileData.gender)      e.gender      = "Gender is required.";
    if (!profileData.email)       e.email       = "Email is required.";
    if (!profileData.country)     e.country     = "Country is required.";
    if (!profileData.address)     e.address     = "Address is required.";
    if (!profileData.phoneNumber) e.phoneNumber = "Phone country code is required.";
    if (!profileData.mobile)      e.mobile      = "Mobile number is required.";
    if (!profileData.cinFront)    e.cinFront    = "ID card front image is required.";
    if (!profileData.cinBack)     e.cinBack     = "ID card back image is required.";
    if (!profileData.video)       e.video       = "Video is required.";
    setErrors((prev) => ({ ...prev, ...e }));
    return Object.keys(e).length === 0;
  };

  /* ── Direct profile submission (no more Subscription / Payment steps) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSending || !validateProfile()) return;

    const userId = localStorage.getItem("userId");
    const token  = localStorage.getItem("token");

    const form = new FormData();
    form.append("userId",      userId);
    form.append("name",        profileData.name);
    form.append("surname",     profileData.surname);
    form.append("dateOfBirth", profileData.dateOfBirth);
    form.append("gender",      profileData.gender);
    form.append("email",       profileData.email);
    form.append("country",     profileData.country);
    form.append("address",     profileData.address);
    form.append("phoneNumber", profileData.phoneNumber);
    form.append("mobile",      profileData.mobile);
    if (profileData.cinFront && typeof profileData.cinFront !== "string")
      form.append("cinFront", profileData.cinFront);
    if (profileData.cinBack && typeof profileData.cinBack !== "string")
      form.append("cinBack", profileData.cinBack);
    if (profileData.video && typeof profileData.video !== "string")
      form.append("video", profileData.video);

    setIsSending(true);
    try {
      await axios.post(`${API_URL}/profiles`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Profile submitted! Awaiting admin verification.");
      setIsSubmitted(true);
    } catch (err) {
      const msg    = err.response?.data?.message || "";
      const detail = err.response?.data?.error   || "";
      console.error("POST /profiles error:", err.response?.data || err);

      if (msg === "Mobile already exists.") {
        setErrors((er) => ({ ...er, mobile: "Mobile number already exists." }));
        toast.error("Mobile number already exists.");
      } else if (msg === "Phone number already exists.") {
        setErrors((er) => ({ ...er, phoneNumber: "Phone number already exists." }));
        toast.error("Phone number already exists.");
      } else if (msg === "A profile already exists for this user.") {
        toast.info("Your profile already exists. Awaiting admin verification.");
        setIsSubmitted(true);
      } else {
        // Shows the real cause returned by the server ("error" field)
        toast.error("Failed to submit profile: " + (detail || msg || err.message));
      }
    } finally {
      setIsSending(false);
    }
  };

  /* ════ RENDER ════ */
  const subtitle = isVerified
    ? "Profile verified"
    : isSubmitted
    ? "Awaiting validation"
    : "Complete your profile";

  return createPortal(
    <>
      <div className="fv-backdrop" />
      <div className="pr-popup">
        <div className="pr-header">
          <button className="pr-header__back" onClick={onClose} type="button" aria-label="Back">
            <ArrowLeft size={12} />
          </button>
          <div className="pr-header__title-wrap">
            <p className="pr-header__title">
              Verification Profile
              {isVerified && <CheckCircle size={16} color="#15803d" />}
            </p>
            <p className="pr-header__sub">{subtitle}</p>
          </div>
          <span className="pr-header__spacer" aria-hidden="true" />
        </div>

        {isSubmitted && !isVerified ? (
          <div className="pr-body">
            <div className="pr-awaiting">⏳ Waiting for admin verification</div>
          </div>
        ) : isVerified ? (
          <VerifiedView profileData={profileData} />
        ) : (
          <FormView
            profileData={profileData}
            errors={errors}
            isSending={isSending}
            setField={setField}
            handleChange={handleChange}
            handleCountryCodeChange={handleCountryCodeChange}
            handleMobileChange={handleMobileChange}
            handleFileChange={handleFileChange}
            cinFrontName={cinFrontName}
            cinBackName={cinBackName}
            videoName={videoName}
            handleSubmit={handleSubmit}
          />
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </>,
    document.body
  );
};

export default Profile;